import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Search, Pencil, Plus } from "lucide-react";
import { formatMatricula } from "@/lib/constants";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

export default function Veiculos() {
  const [list, setList] = useState([]);
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("m") || "");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ matricula: "", vin: "", marca: "", modelo: "", ano: "", motorizacao: "", quilometragem: "", notas: "" });

  const load = () => api.get("/veiculos", { params: { q } }).then((r) => setList(r.data));
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const emptyForm = { matricula: "", vin: "", marca: "", modelo: "", ano: "", motorizacao: "", quilometragem: "", notas: "" };
  const openCreate = () => { setEditId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (v) => {
    setEditId(v.id);
    setForm({ matricula: v.matricula || "", vin: v.vin || "", marca: v.marca || "", modelo: v.modelo || "", ano: v.ano || "", motorizacao: v.motorizacao || "", quilometragem: v.quilometragem || "", notas: v.notas || "" });
    setOpen(true);
  };
  const save = async () => {
    try {
      if (editId) { await api.put(`/veiculos/${editId}`, form); toast.success("Veículo atualizado."); }
      else { await api.post("/veiculos", form); toast.success("Veículo criado."); }
      setOpen(false); setForm(emptyForm); setEditId(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-slate-900">Veículos</h1>
        <Button className="bg-sky-600 hover:bg-sky-700 gap-1.5" data-testid="btn-novo-veiculo" onClick={openCreate}><Plus size={16} /> Novo veículo</Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Editar veículo" : "Novo veículo"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Matrícula</Label><Input className="font-mono uppercase" placeholder="AA-00-00" maxLength={8} value={form.matricula} onChange={(e) => setForm({ ...form, matricula: formatMatricula(e.target.value) })} data-testid="veiculo-matricula" /></div>
            <div><Label className="text-xs">VIN / Nº Quadro</Label><Input className="font-mono" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} data-testid="veiculo-vin" /></div>
            <div><Label className="text-xs">Marca</Label><Input className="uppercase" value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value.toUpperCase() })} data-testid="veiculo-marca" /></div>
            <div><Label className="text-xs">Modelo</Label><Input className="uppercase" value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value.toUpperCase() })} data-testid="veiculo-modelo" /></div>
            <div><Label className="text-xs">Ano</Label><Input value={form.ano} onChange={(e) => setForm({ ...form, ano: e.target.value })} placeholder="ex.: 2015" /></div>
            <div><Label className="text-xs">Motorização</Label><Input value={form.motorizacao} onChange={(e) => setForm({ ...form, motorizacao: e.target.value })} placeholder="ex.: 1.9 TDI" /></div>
            <div><Label className="text-xs">Quilometragem</Label><Input value={form.quilometragem} onChange={(e) => setForm({ ...form, quilometragem: e.target.value })} /></div>
            <div className="col-span-2"><Label className="text-xs">Notas</Label><Input value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button className="bg-sky-600 hover:bg-sky-700" onClick={save} data-testid="btn-guardar-veiculo">{editId ? "Atualizar" : "Guardar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="relative mb-3 max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Matrícula, VIN, marca…" className="pl-9" data-testid="veiculos-search" />
      </div>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
            <tr><th className="text-left px-4 py-2 font-semibold">Matrícula</th><th className="text-left px-4 py-2 font-semibold">Marca</th><th className="text-left px-4 py-2 font-semibold">Modelo</th><th className="text-left px-4 py-2 font-semibold">Ano</th><th className="text-left px-4 py-2 font-semibold">Motorização</th><th className="text-left px-4 py-2 font-semibold">Km</th><th className="text-left px-4 py-2 font-semibold">VIN</th><th className="text-right px-4 py-2 font-semibold">Ações</th></tr>
          </thead>
          <tbody>
            {list.map((v) => (
              <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`veiculo-row-${v.id}`}>
                <td className="px-4 py-2">{v.matricula ? <span className="plate">{v.matricula}</span> : "—"}</td>
                <td className="px-4 py-2 font-medium text-slate-800">{v.marca || "—"}</td>
                <td className="px-4 py-2 text-slate-600">{v.modelo || "—"}</td>
                <td className="px-4 py-2 text-slate-600">{v.ano || "—"}</td>
                <td className="px-4 py-2 text-slate-600">{v.motorizacao || "—"}</td>
                <td className="px-4 py-2 text-slate-600">{v.quilometragem || "—"}</td>
                <td className="px-4 py-2 font-mono text-xs text-slate-500">{v.vin || "—"}</td>
                <td className="px-4 py-2 text-right"><Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => openEdit(v)} data-testid={`veiculo-edit-${v.id}`}><Pencil size={12} /> Editar</Button></td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={8} className="text-center text-slate-400 py-8">Sem veículos.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
